import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, ChevronLeft, Lock, XCircle, ArrowRightLeft, FileText, Plus, CreditCard, Percent, Receipt, Paperclip, Upload, Loader2, CheckCircle2, Clock } from "lucide-react";

const TABS = [
  "Overview", "Ledger", "Charges", "Payments", "Invoices", "Taxes",
  "Discounts", "Approvals", "Documents", "Audit", "Timeline",
];

function fmtMinor(minor, cur = "INR") {
  if (minor === null || minor === undefined) return "—";
  return `${cur} ${(Number(minor) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(d) { return (d || "").slice(0, 16).replace("T", " "); }

const balanceTone = (b) => (b > 0 ? "warning" : b < 0 ? "success" : "neutral");
const statusBadge = { open: "info", closed: "success", void: "error" };

// -------------- Sub-tabs (small, focused components) --------------

function OverviewTab({ view, invoices }) {
  const f = view.folio;
  return (
    <div className="grid grid-cols-3 gap-4">
      <Card className="p-4 col-span-2">
        <div className="text-[10px] uppercase text-neutral-500 mb-2">Bill To</div>
        <div className="text-sm">{f.bill_to?.name || "—"}</div>
        <div className="text-xs text-neutral-400">{f.bill_to?.email}</div>
        <div className="text-xs text-neutral-400">{f.bill_to?.address}</div>
        {f.bill_to?.gstin && <div className="text-xs mono text-neutral-400 mt-1">GSTIN: {f.bill_to.gstin}</div>}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Metric label="Stay" value={f.stay_id || "—"} mono />
          <Metric label="Guest" value={f.guest_id || "—"} mono />
          <Metric label="Purpose" value={f.purpose || "—"} />
        </div>
      </Card>
      <Card className="p-4">
        <div className="text-[10px] uppercase text-neutral-500 mb-2">Balance</div>
        <div className="text-2xl mono">
          <Badge tone={balanceTone(view.balance_minor)}>{fmtMinor(view.balance_minor, f.currency_code)}</Badge>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-neutral-500">Debits:</span> <span className="mono">{fmtMinor(view.total_debit_minor, f.currency_code)}</span></div>
          <div><span className="text-neutral-500">Credits:</span> <span className="mono">{fmtMinor(view.total_credit_minor, f.currency_code)}</span></div>
        </div>
        <div className="mt-3 text-[10px] uppercase text-neutral-500">Invoices Issued</div>
        <div className="text-sm mono">{invoices.length}</div>
      </Card>
    </div>
  );
}

function Metric({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-neutral-500">{label}</div>
      <div className={`text-sm ${mono ? "mono text-[11px]" : ""}`}>{value}</div>
    </div>
  );
}

function LedgerTab({ view }) {
  return (
    <Card className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Posted</th>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2">Template</th>
              <th className="text-left px-3 py-2">Account</th>
              <th className="text-left px-3 py-2">Rev.Centre</th>
              <th className="text-right px-3 py-2">Debit</th>
              <th className="text-right px-3 py-2">Credit</th>
              <th className="text-left px-3 py-2">Group</th>
            </tr>
          </thead>
          <tbody>
            {view.entries.map((e) => (
              <tr key={e.id} className="border-t border-neutral-800/60">
                <td className="px-3 py-2 mono text-[11px]">{fmtDate(e.posted_at_utc)}</td>
                <td className="px-3 py-2 mono text-[11px]">{e.event_name}</td>
                <td className="px-3 py-2 mono text-[11px]">{e.template_code}</td>
                <td className="px-3 py-2 mono">{e.account_code}</td>
                <td className="px-3 py-2 mono text-[11px]">{e.revenue_centre_code || "—"}</td>
                <td className="px-3 py-2 text-right mono">{e.side === "debit" ? fmtMinor(e.amount_minor, e.currency_code) : ""}</td>
                <td className="px-3 py-2 text-right mono">{e.side === "credit" ? fmtMinor(e.amount_minor, e.currency_code) : ""}</td>
                <td className="px-3 py-2 mono text-[10px] text-neutral-500">{(e.posting_group_id || "").slice(-8)}</td>
              </tr>
            ))}
            {view.entries.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-neutral-500">No ledger entries yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ChargesTab({ view, folioId, onPosted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    event_name: "RoomChargeIncurred", amount_minor: 10000,
    template_code_override: "", source_module: "workspace",
  });

  const post = async () => {
    try {
      const payload = {
        event_name: form.event_name,
        idempotency_key: `MANUAL-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        amount_minor: Number(form.amount_minor),
        property_id: view.folio.property_id,
        stay_id: view.folio.stay_id,
        folio_id: folioId,
        currency_code: view.folio.currency_code,
        room_type_code: "STD",
        rate_plan_code: "BAR",
        source_module: form.source_module,
      };
      if (form.template_code_override) payload.template_code_override = form.template_code_override;
      await api.post("/ledger/business-events", payload);
      toast.success("Charge posted");
      setOpen(false);
      onPosted?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)} data-testid="charges-post-btn">
          <Plus className="w-3 h-3 mr-1" /> Post Charge
        </Button>
      </div>
      <LedgerTab view={view} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Post Charge</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Business event</Label>
              <Select value={form.event_name} onValueChange={(v) => setForm({ ...form, event_name: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="RoomChargeIncurred">Room Charge</SelectItem>
                  <SelectItem value="PosSaleCompleted">POS Sale</SelectItem>
                  <SelectItem value="LaundryCharged">Laundry</SelectItem>
                  <SelectItem value="CostumeRented">Costume Rental</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (minor units — paise)</Label>
              <Input type="number" value={form.amount_minor}
                     onChange={(e) => setForm({ ...form, amount_minor: e.target.value })} className="mono" />
            </div>
            <div>
              <Label>Template override (optional)</Label>
              <Input value={form.template_code_override}
                     onChange={(e) => setForm({ ...form, template_code_override: e.target.value })}
                     className="mono" placeholder="e.g. RESTAURANT_SALE" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={post}>Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoicesTab({ folioId, invoices, onIssued }) {
  const issue = async (type) => {
    try {
      const r = await api.post("/invoices", { folio_id: folioId, invoice_type: type });
      toast.success(`${type} ${r.data.invoice_no} generated`);
      onIssued?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const preview = (invId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invId}/html`;
    // Preview via download link with credentials — use fetch to render inline
    fetch(url, { credentials: "include" })
      .then(r => r.text())
      .then(html => {
        const w = window.open("", "_blank");
        w.document.write(html); w.document.close();
      })
      .catch(e => toast.error(String(e)));
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={() => issue("invoice")} data-testid="invoice-issue-btn">
          <FileText className="w-3 h-3 mr-1" /> Issue Invoice
        </Button>
        <Button size="sm" variant="secondary" onClick={() => issue("credit_note")}>Credit Note</Button>
        <Button size="sm" variant="secondary" onClick={() => issue("debit_note")}>Debit Note</Button>
      </div>
      <Card className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Invoice No</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-left px-3 py-2">Issued</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-neutral-800/60">
                <td className="px-3 py-2 mono">{i.invoice_no}</td>
                <td className="px-3 py-2"><Badge tone="neutral">{i.invoice_type}</Badge></td>
                <td className="px-3 py-2 text-right mono">{fmtMinor(i.net_amount_minor, i.currency_code)}</td>
                <td className="px-3 py-2 mono text-[11px]">{fmtDate(i.issued_at_utc)}</td>
                <td className="px-3 py-2"><Badge tone={i.status === "issued" ? "success" : "warning"}>{i.status}</Badge></td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => preview(i.id)}>Preview</Button></td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No invoices yet</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function TimelineTab({ folioId }) {
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    api.get(`/audit-logs?limit=100&entity_type=folio`).then(r => {
      const items = r.data.items || r.data || [];
      setAudit(items.filter(a => a.entity_id === folioId));
    }).catch(() => {});
  }, [folioId]);
  return (
    <Card className="p-0">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Action</th>
            <th className="text-left px-3 py-2">Actor</th>
            <th className="text-left px-3 py-2">Metadata</th>
          </tr>
        </thead>
        <tbody>
          {audit.map((a) => (
            <tr key={a.id} className="border-t border-neutral-800/60">
              <td className="px-3 py-2 mono text-[11px]">{fmtDate(a.created_at)}</td>
              <td className="px-3 py-2 mono">{a.action}</td>
              <td className="px-3 py-2 text-[11px]">{a.actor_email || "—"}</td>
              <td className="px-3 py-2 mono text-[10px] text-neutral-500">{JSON.stringify(a.metadata || {})}</td>
            </tr>
          ))}
          {audit.length === 0 && (
            <tr><td colSpan={4} className="text-center py-8 text-neutral-500">No timeline events yet</td></tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

// -------------- Payments / Taxes / Discounts / Approvals / Documents --------------

const PAY_METHODS = [
  { value: "CASH_PAYMENT", label: "Cash" },
  { value: "CARD_PAYMENT", label: "Card" },
  { value: "UPI_PAYMENT", label: "UPI / Wallet" },
  { value: "BANK_TRANSFER_PAYMENT", label: "Bank Transfer" },
];
const ACCOUNT_METHOD_LABEL = {
  CASH_MAIN: "Cash", AR_CREDIT_CARD: "Card", BANK_MAIN: "Bank / UPI",
};
const toMinor = (major) => Math.round(Number(major || 0) * 100);
const isTaxEntry = (e) => (e.account_code || "").toUpperCase().startsWith("TAX");
const isDiscountEntry = (e) => (e.account_code || "").toUpperCase().startsWith("DISC");
const prettyAccount = (code) =>
  (code || "").replace(/^TAX_|^DISC_/, "").replace(/_/g, " ").toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

function PaymentsTab({ view, folioId, onPosted }) {
  const cur = view.folio.currency_code;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ method: "CASH_PAYMENT", amount: "", reference: "" });

  const payments = useMemo(
    () => view.entries.filter((e) => e.event_name === "PaymentReceived" && e.side === "debit"),
    [view.entries]
  );
  const totalPaid = payments.reduce((s, e) => s + Number(e.amount_minor || 0), 0);
  // True guest balance = net of the AR_GUEST (guest ledger) account:
  // charges debit AR_GUEST, payments/discounts credit it.
  const balanceDue = useMemo(() => {
    return view.entries
      .filter((e) => e.account_code === "AR_GUEST")
      .reduce((s, e) => s + (e.side === "debit" ? 1 : -1) * Number(e.amount_minor || 0), 0);
  }, [view.entries]);

  const record = async () => {
    const minor = toMinor(form.amount);
    if (!minor || minor <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/ledger/business-events", {
        event_name: "PaymentReceived",
        idempotency_key: `PAY-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        amount_minor: minor,
        property_id: view.folio.property_id,
        stay_id: view.folio.stay_id,
        folio_id: folioId,
        currency_code: cur,
        template_code_override: form.method,
        source_module: "workspace",
        reference: form.reference || undefined,
      });
      toast.success("Payment recorded");
      setOpen(false);
      setForm({ method: "CASH_PAYMENT", amount: "", reference: "" });
      onPosted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-6 text-xs">
          <div><span className="text-neutral-500 mr-1">Balance due:</span>
            <Badge tone={balanceTone(balanceDue)}>{fmtMinor(balanceDue, cur)}</Badge></div>
          <div><span className="text-neutral-500 mr-1">Total received:</span>
            <span className="mono text-emerald-400">{fmtMinor(totalPaid, cur)}</span></div>
        </div>
        {view.folio.status === "open" && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="payment-record-btn">
            <CreditCard className="w-3 h-3 mr-1" /> Record Payment
          </Button>
        )}
      </div>
      <Card className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Received</th>
              <th className="text-left px-3 py-2">Method</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((e) => (
              <tr key={e.id} className="border-t border-neutral-800/60" data-testid="payment-row">
                <td className="px-3 py-2 mono text-[11px]">{fmtDate(e.posted_at_utc)}</td>
                <td className="px-3 py-2">{ACCOUNT_METHOD_LABEL[e.account_code] || e.account_code}</td>
                <td className="px-3 py-2 mono text-[11px] text-neutral-400">{e.reference || "—"}</td>
                <td className="px-3 py-2 text-right mono text-emerald-400">{fmtMinor(e.amount_minor, e.currency_code)}</td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-neutral-500">No payments recorded yet</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAY_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount ({cur})</Label>
              <Input type="number" step="0.01" min="0" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     className="mono" placeholder="0.00" data-testid="payment-amount" />
            </div>
            <div>
              <Label>Reference (optional)</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                     placeholder="Txn / receipt no." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={record} disabled={busy} data-testid="payment-submit">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaxesTab({ view }) {
  const cur = view.folio.currency_code;
  const groups = useMemo(() => {
    const m = {};
    view.entries.filter(isTaxEntry).forEach((e) => {
      const k = e.account_code;
      // Tax liability accounts carry a credit normal balance; net = credit − debit.
      const signed = (e.side === "credit" ? 1 : -1) * Number(e.amount_minor || 0);
      m[k] = (m[k] || 0) + signed;
    });
    return Object.entries(m).map(([code, amount_minor]) => ({ code, amount_minor }));
  }, [view.entries]);
  const total = groups.reduce((s, g) => s + g.amount_minor, 0);

  return (
    <Card className="p-0">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
          <tr>
            <th className="text-left px-3 py-2">Tax</th>
            <th className="text-left px-3 py-2">Account</th>
            <th className="text-right px-3 py-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.code} className="border-t border-neutral-800/60" data-testid="tax-row">
              <td className="px-3 py-2 flex items-center gap-2"><Percent className="w-3 h-3 text-neutral-500" />{prettyAccount(g.code)}</td>
              <td className="px-3 py-2 mono text-[11px] text-neutral-400">{g.code}</td>
              <td className="px-3 py-2 text-right mono">{fmtMinor(g.amount_minor, cur)}</td>
            </tr>
          ))}
          {groups.length === 0 && (
            <tr><td colSpan={3} className="text-center py-8 text-neutral-500">No tax lines on this folio</td></tr>
          )}
        </tbody>
        {groups.length > 0 && (
          <tfoot>
            <tr className="border-t border-neutral-700 bg-neutral-900/30">
              <td className="px-3 py-2 text-[11px] uppercase text-neutral-400" colSpan={2}>Total Tax</td>
              <td className="px-3 py-2 text-right mono text-orange-400" data-testid="tax-total">{fmtMinor(total, cur)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </Card>
  );
}

function DiscountsTab({ view, folioId, onPosted }) {
  const cur = view.folio.currency_code;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ amount: "", reason: "" });

  const groups = useMemo(() => {
    const m = {};
    view.entries.filter(isDiscountEntry).forEach((e) => {
      const k = e.account_code;
      // Discount contra-revenue accounts carry a debit normal balance; net = debit − credit.
      const signed = (e.side === "debit" ? 1 : -1) * Number(e.amount_minor || 0);
      m[k] = (m[k] || 0) + signed;
    });
    return Object.entries(m).map(([code, amount_minor]) => ({ code, amount_minor }));
  }, [view.entries]);
  const total = groups.reduce((s, g) => s + g.amount_minor, 0);

  const apply = async () => {
    const minor = toMinor(form.amount);
    if (!minor || minor <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ledger/business-events", {
        event_name: "DiscountApplied",
        idempotency_key: `DISC-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        amount_minor: minor,
        property_id: view.folio.property_id,
        stay_id: view.folio.stay_id,
        folio_id: folioId,
        currency_code: cur,
        template_code_override: "MANAGER_DISCOUNT",
        source_module: "workspace",
        note: form.reason || undefined,
      });
      if (r.data?.approval_required || r.data?.status === "pending_approval") {
        toast.success("Discount submitted for approval");
      } else {
        toast.success("Discount applied");
      }
      setOpen(false); setForm({ amount: "", reason: "" });
      onPosted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs"><span className="text-neutral-500 mr-1">Total discounts:</span>
          <span className="mono text-orange-400">{fmtMinor(total, cur)}</span></div>
        {view.folio.status === "open" && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="discount-apply-btn">
            <Percent className="w-3 h-3 mr-1" /> Apply Discount
          </Button>
        )}
      </div>
      <Card className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Discount</th>
              <th className="text-left px-3 py-2">Account</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.code} className="border-t border-neutral-800/60" data-testid="discount-row">
                <td className="px-3 py-2">{prettyAccount(g.code)}</td>
                <td className="px-3 py-2 mono text-[11px] text-neutral-400">{g.code}</td>
                <td className="px-3 py-2 text-right mono text-orange-400">{fmtMinor(g.amount_minor, cur)}</td>
              </tr>
            ))}
            {groups.length === 0 && (
              <tr><td colSpan={3} className="text-center py-8 text-neutral-500">No discounts on this folio</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apply Discount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-neutral-500">
              Posts a Manager Discount against the guest ledger. Over-threshold amounts may require approval.
            </div>
            <div>
              <Label>Amount ({cur})</Label>
              <Input type="number" step="0.01" min="0" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     className="mono" placeholder="0.00" data-testid="discount-amount" />
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                     placeholder="e.g. loyalty goodwill" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={apply} disabled={busy} data-testid="discount-submit">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApprovalsTab({ view, folioId }) {
  const [items, setItems] = useState(null);
  const relatedIds = useMemo(() => {
    const s = new Set([folioId]);
    view.entries.forEach((e) => e.posting_group_id && s.add(e.posting_group_id));
    return s;
  }, [view.entries, folioId]);

  useEffect(() => {
    api.get("/workflow-instances?limit=200")
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : (r.data.items || []);
        const mine = all.filter((w) => {
          const ctx = w.context || {};
          return (
            relatedIds.has(w.entity_id) ||
            ctx.folio_id === folioId ||
            ctx?.dimensions?.folio_id === folioId
          );
        });
        setItems(mine);
      })
      .catch(() => setItems([]));
  }, [folioId, relatedIds]);

  if (items === null) return <Card className="p-6 text-center text-neutral-500">Loading approvals…</Card>;

  return (
    <Card className="p-0">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
          <tr>
            <th className="text-left px-3 py-2">Workflow</th>
            <th className="text-left px-3 py-2">Current State</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Raised</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr key={w.id} className="border-t border-neutral-800/60" data-testid="approval-row">
              <td className="px-3 py-2 mono text-[11px]">{w.definition_code || w.domain || "—"}</td>
              <td className="px-3 py-2">{w.current_state || "—"}</td>
              <td className="px-3 py-2">
                {w.is_complete
                  ? <Badge tone="success"><CheckCircle2 className="w-3 h-3 mr-1 inline" />{w.outcome || "complete"}</Badge>
                  : <Badge tone="warning"><Clock className="w-3 h-3 mr-1 inline" />pending</Badge>}
              </td>
              <td className="px-3 py-2 mono text-[11px]">{fmtDate(w.created_at)}</td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={4} className="text-center py-8 text-neutral-500">No approvals linked to this folio</td></tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

function DocumentsTab({ folioId }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get(`/media/v1/by-entity?entity_type=folio&entity_id=${folioId}`)
      .then((r) => setDocs(r.data.items || []))
      .catch(() => setDocs([]));
  };
  useEffect(() => { load(); }, [folioId]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", "folio");
      fd.append("entity_id", folioId);
      await api.post("/media/v1/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  const viewDoc = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/media/v1/${id}`, "_blank");
  const fmtSize = (b) => (b == null ? "—" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <label className="inline-flex">
          <input type="file" accept="image/*,application/pdf" className="hidden"
                 data-testid="document-file-input"
                 onChange={(e) => onUpload(e.target.files?.[0])} />
          <Button asChild size="sm" disabled={uploading}>
            <span data-testid="document-upload-btn">
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              Upload Document
            </span>
          </Button>
        </label>
      </div>
      <Card className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
            <tr>
              <th className="text-left px-3 py-2">File</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Size</th>
              <th className="text-left px-3 py-2">Uploaded</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(docs || []).map((d) => (
              <tr key={d.id} className="border-t border-neutral-800/60" data-testid="document-row">
                <td className="px-3 py-2 flex items-center gap-2"><Paperclip className="w-3 h-3 text-neutral-500" />{d.filename}</td>
                <td className="px-3 py-2 mono text-[11px] text-neutral-400">{d.content_type}</td>
                <td className="px-3 py-2 text-right mono text-[11px]">{fmtSize(d.size)}</td>
                <td className="px-3 py-2 mono text-[11px]">{fmtDate(d.created_at)}</td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => viewDoc(d.id)}>View</Button></td>
              </tr>
            ))}
            {docs !== null && docs.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-neutral-500">No documents attached to this folio</td></tr>
            )}
            {docs === null && (
              <tr><td colSpan={5} className="text-center py-8 text-neutral-500">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// -------------- Main workspace --------------

export default function FolioWorkspace() {
  const { id } = useParams();
  const [view, setView] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [xferOpen, setXferOpen] = useState(false);
  const [xferForm, setXferForm] = useState({ target_folio_id: "", posting_group_id: "", reason: "" });

  const load = async () => {
    try {
      const [v, inv] = await Promise.all([
        api.get(`/folios/${id}`),
        api.get(`/invoices?folio_id=${id}&page_size=50`),
      ]);
      setView(v.data);
      setInvoices(inv.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const groups = useMemo(() => {
    if (!view) return [];
    const seen = new Set();
    return view.entries.filter(e => {
      if (!e.posting_group_id || seen.has(e.posting_group_id)) return false;
      seen.add(e.posting_group_id); return true;
    });
  }, [view]);

  const doClose = async () => {
    try { await api.post(`/folios/${id}/close`); toast.success("Folio closed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doVoid = async () => {
    if (!window.confirm("Void this folio? This is permanent.")) return;
    try { await api.post(`/folios/${id}/void`, { reason: "user requested" }); toast.success("Folio voided"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doTransfer = async () => {
    try {
      await api.post(`/folios/${id}/transfer`, xferForm);
      toast.success("Transferred (ledger unchanged, allocation recorded)");
      setXferOpen(false);
      setXferForm({ target_folio_id: "", posting_group_id: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!view) return <Card className="p-6 text-center text-neutral-500">Loading folio…</Card>;
  const f = view.folio;

  return (
    <div className="space-y-4" data-testid="folio-workspace">
      <PageHeader
        icon={Wallet}
        title={<span>Folio <span className="mono text-neutral-500">{f.folio_no}</span></span>}
        subtitle={
          <div className="flex items-center gap-2 text-xs">
            <Badge tone="neutral">{f.folio_type}</Badge>
            <Badge tone={statusBadge[f.status] || "neutral"}>{f.status}</Badge>
            <span className="text-neutral-500 mono">Property: {f.property_id}</span>
            {f.stay_id && <span className="text-neutral-500 mono">Stay: {f.stay_id}</span>}
          </div>
        }
        action={
          <div className="flex gap-2">
            <Link to="/finance/folios"><Button variant="ghost" size="sm"><ChevronLeft className="w-3 h-3 mr-1" /> Back</Button></Link>
            {f.status === "open" && (
              <>
                <Button size="sm" variant="secondary" onClick={() => setXferOpen(true)} data-testid="folio-transfer-btn">
                  <ArrowRightLeft className="w-3 h-3 mr-1" /> Transfer
                </Button>
                <Button size="sm" variant="secondary" onClick={doClose} data-testid="folio-close-btn">
                  <Lock className="w-3 h-3 mr-1" /> Close
                </Button>
                <Button size="sm" variant="destructive" onClick={doVoid} data-testid="folio-void-btn">
                  <XCircle className="w-3 h-3 mr-1" /> Void
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap gap-1 border-b border-neutral-800">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`folio-tab-${t.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs mono uppercase tracking-wider transition
              ${tab === t
                ? "text-orange-400 border-b-2 border-orange-400"
                : "text-neutral-500 hover:text-neutral-300"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "Overview" && <OverviewTab view={view} invoices={invoices} />}
        {tab === "Ledger" && <LedgerTab view={view} />}
        {tab === "Charges" && <ChargesTab view={view} folioId={id} onPosted={load} />}
        {tab === "Invoices" && <InvoicesTab folioId={id} invoices={invoices} onIssued={load} />}
        {tab === "Timeline" && <TimelineTab folioId={id} />}
        {tab === "Payments" && <PaymentsTab view={view} folioId={id} onPosted={load} />}
        {tab === "Taxes" && <TaxesTab view={view} />}
        {tab === "Discounts" && <DiscountsTab view={view} folioId={id} onPosted={load} />}
        {tab === "Approvals" && <ApprovalsTab view={view} folioId={id} />}
        {tab === "Documents" && <DocumentsTab folioId={id} />}
        {tab === "Audit" && <TimelineTab folioId={id} />}
      </div>

      <Dialog open={xferOpen} onOpenChange={setXferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transfer Charge</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-[11px] text-neutral-500">
              The ledger is <span className="text-orange-400">immutable</span>. A transfer creates an
              <span className="mono"> allocation record</span> that redirects the charge in the folio view.
              No ledger row is modified.
            </div>
            <div>
              <Label>Target Folio ID</Label>
              <Input value={xferForm.target_folio_id}
                     onChange={(e) => setXferForm({ ...xferForm, target_folio_id: e.target.value })}
                     className="mono" data-testid="transfer-target" />
            </div>
            <div>
              <Label>Posting group to transfer</Label>
              <Select value={xferForm.posting_group_id}
                      onValueChange={(v) => setXferForm({ ...xferForm, posting_group_id: v })}>
                <SelectTrigger data-testid="transfer-group"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.posting_group_id} value={g.posting_group_id}>
                      {g.event_name} — {fmtMinor(g.amount_minor)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={xferForm.reason}
                     onChange={(e) => setXferForm({ ...xferForm, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setXferOpen(false)}>Cancel</Button>
            <Button onClick={doTransfer} data-testid="transfer-submit">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
